import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Factory } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    product_type: '',
    quantity: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create FormData object
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      // Send to PHP script
      const response = await fetch('/send-enquiry.php', {
        method: 'POST',
        body: formDataToSend,
      });

      const responseText = await response.text();

      if (response.ok && responseText.trim() === 'success') {
        setSubmitStatus({ 
          type: 'success', 
          message: 'Quote request submitted successfully! We will contact you soon.' 
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          product_type: '',
          quantity: '',
          message: '',
        });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Error submitting quote request:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Failed to submit quote request. Please try again or call us directly.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="font-inter" data-testid="contact-page">
      {/* Header */}
      <section className="bg-[#2B2B2B] text-white py-16" data-testid="contact-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-poppins font-bold text-4xl md:text-5xl mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-gray-300">
            Get in touch for custom pallet solutions and quotes
          </p>
          <div className="w-24 h-1 bg-[#B11217] mt-4"></div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 bg-white" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2 className="font-poppins font-bold text-2xl text-[#2B2B2B] mb-6">
                Get in Touch
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-address">
                  <div className="w-12 h-12 bg-[#B11217] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-poppins font-semibold text-lg text-[#2B2B2B] mb-1">Office Address</h3>
                    <p className="text-gray-600">
                      Gandhidham, Kachchh<br />
                      Gujarat, India - 370201
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-factory">
                  <div className="w-12 h-12 bg-[#B11217] rounded-full flex items-center justify-center flex-shrink-0">
                    <Factory size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-poppins font-semibold text-lg text-[#2B2B2B] mb-1">Factory Address</h3>
                    <p className="text-gray-600">
                      Varsamedi, near Matiyadev Temple,<br />
                      Anjar Highway, Kachchh,<br />
                      Gujarat - 370110, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-phone">
                  <div className="w-12 h-12 bg-[#B11217] rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-poppins font-semibold text-lg text-[#2B2B2B] mb-1">Phone</h3>
                    <p className="text-gray-600">
                      +91 99883 51111<br />
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-email">
                  <div className="w-12 h-12 bg-[#B11217] rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-poppins font-semibold text-lg text-[#2B2B2B] mb-1">Email</h3>
                    <p className="text-gray-600">
                      sales@sitamaniglobal.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-hours">
                  <div className="w-12 h-12 bg-[#B11217] rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-poppins font-semibold text-lg text-[#2B2B2B] mb-1">Business Hours</h3>
                    <p className="text-gray-600">
                      Monday - Saturday<br />
                      9:00 AM - 6:00 PM IST
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quote Request Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#F2F2F2] p-8 rounded-lg">
                <h2 className="font-poppins font-bold text-2xl text-[#2B2B2B] mb-6">
                  Request a Quote
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="quote-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block font-inter font-medium text-[#2B2B2B] mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#B11217]"
                        placeholder="Your name"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block font-inter font-medium text-[#2B2B2B] mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#B11217]"
                        placeholder="your.email@example.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block font-inter font-medium text-[#2B2B2B] mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#B11217]"
                        placeholder="+91 XXXXX XXXXX"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block font-inter font-medium text-[#2B2B2B] mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#B11217]"
                        placeholder="Your company name"
                        data-testid="input-company"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="product_type" className="block font-inter font-medium text-[#2B2B2B] mb-2">
                        Product Type *
                      </label>
                      <select
                        id="product_type"
                        name="product_type"
                        value={formData.product_type}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#B11217] bg-white"
                        data-testid="select-product-type"
                      >
                        <option value="">Select a product</option>
                        <option value="Pine Wood Pallets">Pine Wood Pallets</option>
                        <option value="Hardwood Pallets">Hardwood Pallets</option>
                        <option value="Jungle Wood Pallets">Jungle Wood Pallets</option>
                        <option value="Two-Way Pallets">Two-Way Pallets</option>
                        <option value="Four-Way Pallets">Four-Way Pallets</option>
                        <option value="Heavy-Duty Pallets">Heavy-Duty Pallets</option>
                        <option value="Wooden Boxes">Wooden Boxes</option>
                        <option value="Wooden Crates">Wooden Crates</option>
                        <option value="Plywood Boxes">Plywood Boxes</option>
                        <option value="Export Packaging">Export Packaging</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="quantity" className="block font-inter font-medium text-[#2B2B2B] mb-2">
                        Quantity *
                      </label>
                      <input
                        type="text"
                        id="quantity"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#B11217]"
                        placeholder="e.g., 100 units, 500 pallets"
                        data-testid="input-quantity"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block font-inter font-medium text-[#2B2B2B] mb-2">
                      Additional Requirements
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#B11217] resize-none"
                      placeholder="Please specify dimensions, load capacity, or any other requirements..."
                      data-testid="textarea-message"
                    ></textarea>
                  </div>

                  {submitStatus && (
                    <div
                      className={`p-4 rounded ${
                        submitStatus.type === 'success'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                      data-testid="submit-status"
                    >
                      {submitStatus.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-[#B11217] text-white px-8 py-4 font-poppins font-semibold text-sm uppercase tracking-wide hover:bg-[#8b0e12] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-[#F2F2F2]" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-bold text-3xl text-[#2B2B2B] mb-8 text-center">
            Find Us
          </h2>
          <div className="rounded-lg overflow-hidden shadow-lg h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d117812.88939392086!2d70.05097089453127!3d23.077522399999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3950bba7e84d22bd%3A0x76edb2e2c3d3a93b!2sGandhidham%2C%20Gujarat!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Sita Mani Global Location"
              data-testid="google-map"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
